import {
    getFirestore, collection, doc, getDoc, getDocs, setDoc,
    deleteDoc, onSnapshot, serverTimestamp, query, orderBy
} from "firebase/firestore";
import { app } from "../firebase-config.js";

const db = getFirestore(app);

// Default System Docs Configuration
const DEFAULT_DOCS = [
    { id: 'terms', label: '이용약관' },
    { id: 'privacy', label: '개인정보처리방침' },
    { id: 'about', label: '서비스 소개' }
];

export const AdminDocsService = {
    /**
     * initializes and ensures default system documents exist.
     * Handles migration of legacy documents (missing titles).
     */
    ensureDefaultDocs: async () => {
        console.log("[AdminDocsService] Checking Default Dosc...");
        try {
            for (const def of DEFAULT_DOCS) {
                const docRef = doc(db, 'system_docs', def.id);
                const snap = await getDoc(docRef);

                if (!snap.exists()) {
                    console.log(`[AdminDocsService] Creating missing doc: ${def.id}`);
                    await setDoc(docRef, {
                        title: def.label,
                        content: `# ${def.label}\n\n내용을 입력하세요.`,
                        updatedAt: serverTimestamp(),
                        isSystem: true
                    });
                } else {
                    // Migration: Ensure title exists
                    const data = snap.data();
                    if (!data.title) {
                        console.log(`[AdminDocsService] Migrating legacy doc title: ${def.id}`);
                        await setDoc(docRef, { title: def.label }, { merge: true });
                    }
                }
            }
        } catch (e) {
            console.error("[AdminDocsService] Migration Failed:", e);
        }
    },

    /**
     * Subscribes to the system_docs collection.
     * @param {function} callback - Function to receive the list of docs
     * @returns {function} - Unsubscribe function
     */
    subscribeDocs: (callback) => {
        // We order by client-side usually, but fetching all is fine.
        // Removed orderBy('title') to prevent hiding docs without titles (safety)
        const q = query(collection(db, 'system_docs'));
        return onSnapshot(q, (snapshot) => {
            const docs = [];
            snapshot.forEach((doc) => {
                docs.push({ id: doc.id, ...doc.data() });
            });
            // Client-side sort: System docs first, then alphabetical
            docs.sort((a, b) => {
                if (a.isSystem && !b.isSystem) return -1;
                if (!a.isSystem && b.isSystem) return 1;
                return (a.title || a.id).localeCompare(b.title || b.id);
            });
            callback(docs);
        });
    },

    /**
     * Gets a single document by ID.
     */
    getDoc: async (docId) => {
        try {
            const snap = await getDoc(doc(db, 'system_docs', docId));
            if (snap.exists()) {
                return { id: snap.id, ...snap.data() };
            }
            return null;
        } catch (e) {
            console.error("[AdminDocsService] Get Error:", e);
            throw e;
        }
    },

    /**
     * Saves or Updates a document.
     */
    saveDoc: async (docId, title, content, isSystem = false, translations = null) => {
        try {
            const docRef = doc(db, 'system_docs', docId);
            const payload = {
                title,
                content,
                isSystem,
                updatedAt: serverTimestamp()
            };
            if (translations) {
                payload.translations = translations;
            }
            await setDoc(docRef, payload, { merge: true });
            return true;
        } catch (e) {
            console.error("[AdminDocsService] Save Error:", e);
            throw e;
        }
    },

    /**
     * Deletes a document.
     * Blocks deletion of System docs.
     */
    deleteDoc: async (docId) => {
        // Double check system doc safety
        if (DEFAULT_DOCS.find(d => d.id === docId)) {
            throw new Error("시스템 기본 문서는 삭제할 수 없습니다.");
        }
        try {
            await deleteDoc(doc(db, 'system_docs', docId));
            return true;
        } catch (e) {
            console.error("[AdminDocsService] Delete Error:", e);
            throw e;
        }
    },

    /**
     * Registers the document to the System Menu.
     * Uses AdminMenuEditor logic (or mimics it) by updating Firestore directly.
     * Since AdminMenuEditor logic is complex (reading nav structure), 
     * we might keep this part in the UI or move pure DB logic here.
     * For now, let's keep menu registration in the UI or a separate MenuService.
     * But we can provide a helper to get the link path.
     */
    getDocLink: (docId) => {
        // Logic from SystemMenuDrawer: docId implies InfoModal
        return `(Modal: ${docId})`;
    }
};
